import { Supporter } from '@/types';
import { ASSIGNEE_UNASSIGNED_LABEL } from '@/constants/listGridFilters';
import { normalizePhoneForFilter, stripUnedifyingCharacters } from '@/util';
import { withoutEmptyFilterOperators } from '@/util/gridFilterOperators';
import {
  getGridNumericOperators,
  getGridStringOperators,
  GridColDef,
  GridFilterInputMultipleValue,
  GridFilterInputMultipleValueProps,
  GridFilterInputValue,
  GridFilterInputValueProps,
  GridFilterItem,
  GridFilterOperator,
  GridValidRowModel,
} from '@mui/x-data-grid';

export function StripUnedifyingInputValue(props: GridFilterInputValueProps) {
  const { item, applyValue } = props;

  const stripUnedifyingFilterChange = (valueIn: GridFilterItem) => {
    applyValue(
      typeof valueIn.value === 'string'
        ? { ...valueIn, value: stripUnedifyingCharacters(valueIn.value as string) }
        : valueIn
    );
  };

  return <GridFilterInputValue {...props} item={item} applyValue={stripUnedifyingFilterChange} />;
}

export function StripPhoneDigitsInputValue(props: GridFilterInputValueProps) {
  const { item, applyValue } = props;

  const stripPhoneDigitsFilterChange = (valueIn: GridFilterItem) => {
    applyValue(
      typeof valueIn.value === 'string'
        ? { ...valueIn, value: normalizePhoneForFilter(valueIn.value as string) }
        : valueIn
    );
  };

  return (
    <GridFilterInputValue {...props} item={item} applyValue={stripPhoneDigitsFilterChange} />
  );
}

export function StripPhoneDigitsInputMultipleValue(props: GridFilterInputMultipleValueProps) {
  const { item, applyValue } = props;

  const stripPhoneDigitsFilterApply = (valueIn: GridFilterItem) => {
    const raw = valueIn.value;
    if (!Array.isArray(raw)) {
      applyValue(valueIn);
      return;
    }
    const mappedValue = raw.map((entry) =>
      typeof entry === 'string' ? normalizePhoneForFilter(entry) : entry
    );
    if (!mappedValue.includes('')) {
      applyValue({ ...valueIn, value: mappedValue });
    }
  };

  return (
    <GridFilterInputMultipleValue
      {...props}
      item={item}
      applyValue={stripPhoneDigitsFilterApply}
    />
  );
}

export function StripUnedifyingInputMultipleValue(props: GridFilterInputMultipleValueProps) {
  const { item, applyValue } = props;

  const stripUnedifyingFilterApply = (valueIn: GridFilterItem) => {
    const raw = valueIn.value;
    if (!Array.isArray(raw)) {
      applyValue(valueIn);
      return;
    }
    const mappedValue = raw.map((entry) =>
      typeof entry === 'string' ? stripUnedifyingCharacters(entry) : entry
    );
    if (!mappedValue.includes('')) {
      applyValue({ ...valueIn, value: mappedValue });
    }
  };

  return (
    <GridFilterInputMultipleValue {...props} item={item} applyValue={stripUnedifyingFilterApply} />
  );
}

export const redisBetterOperators: GridFilterOperator[] = getGridStringOperators().map(
  (operator) => ({
    ...operator,
    InputComponent: ['contains', 'startsWith', 'endsWith', 'equals'].includes(operator.value)
      ? StripUnedifyingInputValue
      : operator.value === 'isAnyOf'
        ? StripUnedifyingInputMultipleValue
        : operator.InputComponent,
  })
);

const ALLOWED_ID_OPERATOR_VALUES = new Set([
  '=',
  '!=',
  '<',
  '>',
  '<=',
  '>=',
  'contains',
  'startsWith',
  'endsWith',
  'isAnyOf',
]);

const NUMERIC_ID_OPERATOR_VALUES = new Set(['=', '!=', '<', '>', '<=', '>=', 'isAnyOf']);

const coerceIdFilterScalar = (value: unknown): unknown => {
  if (typeof value === 'string' && value.trim() !== '') {
    const num = Number(value);
    return Number.isNaN(num) ? value : num;
  }
  return value;
};

const coerceIdFilterItemValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(coerceIdFilterScalar);
  }
  return coerceIdFilterScalar(value);
};

/** Grid filter inputs are strings; row ids are numbers — coerce before numeric comparisons. */
export const withNumericIdFilterValueCoercion = (
  operators: GridFilterOperator[]
): GridFilterOperator[] =>
  operators.map((operator) => {
    if (!NUMERIC_ID_OPERATOR_VALUES.has(operator.value)) {
      return operator;
    }
    return {
      ...operator,
      getApplyFilterFn: (filterItem, column) => {
        const coercedItem = {
          ...filterItem,
          value: coerceIdFilterItemValue(filterItem.value),
        };
        return operator.getApplyFilterFn(coercedItem, column as GridColDef<GridValidRowModel>);
      },
    };
  });

const baseIdOperators = getGridNumericOperators()
  .concat(...getGridStringOperators())
  .filter(
    (operator, index, operators) =>
      ALLOWED_ID_OPERATOR_VALUES.has(operator.value) &&
      (operator.value !== 'isAnyOf' ||
        operators.findIndex(({ value }) => value === 'isAnyOf') === index)
  );

export const idOperators = withNumericIdFilterValueCoercion(baseIdOperators);

/** Numeric-only id filters for Contacts tables (preview, new list, etc.). */
export const contactIdFilterOperators = withNumericIdFilterValueCoercion(
  withoutEmptyFilterOperators(getGridNumericOperators())
);

/**
 * Name columns on Redis-backed tables — preserve hyphens, apostrophes, and spaces in
 * filter input (do not use stripUnedifyingCharacters).
 */
export const redisNameFilterOperators: GridFilterOperator[] = getGridStringOperators();

/**
 * Notes column on Redis-backed tables — preserve spaces/punctuation in filter input
 * (do not use stripUnedifyingCharacters) and omit is any of.
 */
export const notesFilterOperators: GridFilterOperator[] = getGridStringOperators().filter(
  (operator) => operator.value !== 'isAnyOf'
);

/** List detail table: Id column — id filters without contains. */
export const detailIdFilterOperators = idOperators.filter(
  (operator) => operator.value !== 'contains'
);

const normalizePhoneFilterItem = (filterItem: GridFilterItem): GridFilterItem => {
  if (Array.isArray(filterItem.value)) {
    return {
      ...filterItem,
      value: filterItem.value.map((entry) =>
        typeof entry === 'string' ? normalizePhoneForFilter(entry) : entry
      ),
    };
  }
  if (typeof filterItem.value === 'string') {
    return { ...filterItem, value: normalizePhoneForFilter(filterItem.value) };
  }
  return filterItem;
};

/**
 * String filters for obscured phone columns — matches against the full stored number (digits only),
 * not the masked display (e.g. 04** *** 708).
 */
export const createFullPhoneFilterOperators = <R extends GridValidRowModel>(
  getPhone: (row: R) => string
): GridFilterOperator<R, string, string>[] =>
  redisBetterOperators.map((operator) => ({
    ...operator,
    InputComponent: ['contains', 'startsWith', 'endsWith', 'equals'].includes(operator.value)
      ? StripPhoneDigitsInputValue
      : operator.value === 'isAnyOf'
        ? StripPhoneDigitsInputMultipleValue
        : operator.InputComponent,
    getApplyFilterFn: (filterItem, column) => {
      const comparePhone = operator.getApplyFilterFn(
        normalizePhoneFilterItem(filterItem),
        column as GridColDef<GridValidRowModel>
      );
      if (!comparePhone) {
        return null;
      }
      return (value, row, col, apiRef) =>
        comparePhone(normalizePhoneForFilter(getPhone(row)), row, col, apiRef);
    },
  })) as GridFilterOperator<R, string, string>[];

export const searchResultsPhoneFilterOperators = createFullPhoneFilterOperators<Supporter>(
  (row) => row.phone ?? ''
);

const ASSIGNEE_NAME_TEXT_OPERATORS = new Set([
  'contains',
  'doesNotContain',
  'equals',
  'doesNotEqual',
  'startsWith',
  'endsWith',
]);

export { ASSIGNEE_UNASSIGNED_LABEL };

export const getAssigneeDisplayName = (assigneeName?: string) =>
  assigneeName ? assigneeName : ASSIGNEE_UNASSIGNED_LABEL;

const isAssigneeUnassigned = (row: Supporter) => !row.assigneeName;

/**
 * Assigned-to column filters compare display names (chip label / "Unassigned"), not the Redis
 * sort key or raw empty assigneeName stored on the row.
 */
export const assigneeNameFilterOperators: GridFilterOperator<Supporter, string, string>[] =
  redisBetterOperators.map((operator) => {
    if (operator.value === 'isEmpty') {
      return {
        ...operator,
        getApplyFilterFn: () => (_value, row) => isAssigneeUnassigned(row),
      };
    }
    if (operator.value === 'isNotEmpty') {
      return {
        ...operator,
        getApplyFilterFn: () => (_value, row) => !isAssigneeUnassigned(row),
      };
    }
    return {
      ...operator,
      InputComponent: ASSIGNEE_NAME_TEXT_OPERATORS.has(operator.value)
        ? StripUnedifyingInputValue
        : operator.InputComponent,
      getApplyFilterFn: (filterItem, column) => {
        const compareDisplayName = operator.getApplyFilterFn(
          filterItem,
          column as GridColDef<GridValidRowModel>
        );
        if (!compareDisplayName) {
          return null;
        }
        return (_value, row, col, apiRef) =>
          compareDisplayName(getAssigneeDisplayName(row.assigneeName), row, col, apiRef);
      },
    };
  }) as GridFilterOperator<Supporter, string, string>[];
